import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

// Geometry for the first-login tour's spotlight cutout.
//
// Base UI's Popover positions the coachmark *card* for us, but the lit cutout is
// our own element, so it needs the target's live viewport rect. That is what this
// hook provides — plus the two things Floating UI deliberately does not do:
// scroll an off-screen target into view, and tell us when a target is missing.

export interface AnchorRect {
  top: number
  left: number
  width: number
  height: number
}

/** How long to wait for an anchor that may still be mounting. */
const LATE_ANCHOR_TIMEOUT_MS = 800
/** Cap on the post-scroll re-measure loop, so a smooth scroll can settle. */
const MAX_SETTLE_FRAMES = 30

function prefersReducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
}

/**
 * Returns the first *visible* element carrying `tourId`.
 *
 * Visibility matters because the refer CTA is rendered twice — once in the
 * desktop navbar and once in the mobile nav — and whichever is outside the
 * current breakpoint is `display: none`. A hidden element reports an all-zero
 * bounding rect, so taking the first match blindly would pin the spotlight to
 * the top-left corner of the screen.
 */
export function findVisibleAnchor(tourId: string): HTMLElement | null {
  const nodes = document.querySelectorAll<HTMLElement>(`[data-tour-id="${tourId}"]`)

  for (const node of nodes) {
    // A zero-area rect is the signal: `display: none` (Tailwind's `hidden`, which
    // is how the out-of-breakpoint nav variant is suppressed) reports all zeros.
    // Checking the rect alone is enough — and unlike `offsetParent` it holds for
    // positioned elements too.
    const rect = node.getBoundingClientRect()
    if (rect.width > 0 && rect.height > 0) {
      return node
    }
  }

  return null
}

function toRect(element: HTMLElement): AnchorRect {
  const { top, left, width, height } = element.getBoundingClientRect()
  return { top, left, width, height }
}

function isOutsideViewport(rect: AnchorRect): boolean {
  return rect.top < 0 || rect.top + rect.height > window.innerHeight
}

export function useElementRect(
  tourId: string | null,
  { onMissing }: { onMissing?: () => void } = {}
): { rect: AnchorRect | null; element: HTMLElement | null } {
  const [element, setElement] = useState<HTMLElement | null>(null)
  const [rect, setRect] = useState<AnchorRect | null>(null)

  // Kept in a ref so the resolve effect below does not re-run (and restart its
  // timeout) every time the caller passes a fresh callback identity.
  const onMissingRef = useRef(onMissing)
  onMissingRef.current = onMissing

  // Resolve the anchor, tolerating one that has not mounted yet. The action-items
  // section is the real case: it renders a skeleton first, and the whole section
  // is behind another team's feature flag, so it may never appear at all.
  useEffect(() => {
    if (!tourId) {
      setElement(null)
      return
    }

    const found = findVisibleAnchor(tourId)
    if (found) {
      setElement(found)
      return
    }

    setElement(null)

    let settled = false
    const finish = (node: HTMLElement | null) => {
      if (settled) return
      settled = true
      observer.disconnect()
      window.clearTimeout(timeout)
      if (node) {
        setElement(node)
      } else {
        onMissingRef.current?.()
      }
    }

    const observer = new MutationObserver(() => {
      const late = findVisibleAnchor(tourId)
      if (late) finish(late)
    })
    observer.observe(document.body, { childList: true, subtree: true })

    const timeout = window.setTimeout(() => finish(null), LATE_ANCHOR_TIMEOUT_MS)

    return () => {
      settled = true
      observer.disconnect()
      window.clearTimeout(timeout)
    }
  }, [tourId])

  const measure = useCallback(() => {
    setRect((previous) => {
      if (!element) return null
      const next = toRect(element)
      // Bail on identical values so scroll/resize storms do not re-render.
      if (
        previous &&
        previous.top === next.top &&
        previous.left === next.left &&
        previous.width === next.width &&
        previous.height === next.height
      ) {
        return previous
      }
      return next
    })
  }, [element])

  // Scroll the target into view before the first measure, otherwise a
  // below-the-fold anchor gets a spotlight drawn off-screen.
  useLayoutEffect(() => {
    if (!element) {
      setRect(null)
      return
    }

    measure()

    if (!isOutsideViewport(toRect(element))) return

    element.scrollIntoView({
      block: 'center',
      inline: 'nearest',
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    })

    // Re-measure until the rect stops moving. `scrollend` would be tidier but is
    // not available everywhere we ship, so this polls a bounded number of frames.
    let frame = 0
    let previousTop: number | null = null
    let raf = 0
    const settle = () => {
      const current = toRect(element)
      measure()
      const stable = previousTop !== null && Math.abs(current.top - previousTop) < 0.5
      previousTop = current.top
      frame += 1
      if (!stable && frame < MAX_SETTLE_FRAMES) {
        raf = requestAnimationFrame(settle)
      }
    }
    raf = requestAnimationFrame(settle)

    return () => cancelAnimationFrame(raf)
  }, [element, measure])

  // Track the target through scrolling, resizing, and its own size changes.
  useEffect(() => {
    if (!element) return

    let raf = 0
    const schedule = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(measure)
    }

    window.addEventListener('resize', schedule, { passive: true })
    // Capture phase so scrolling inside any ancestor container is caught too.
    window.addEventListener('scroll', schedule, { passive: true, capture: true })

    // Guarded because neither Vitest setup polyfills ResizeObserver.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule)
    observer?.observe(element)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', schedule)
      window.removeEventListener('scroll', schedule, { capture: true })
      observer?.disconnect()
    }
  }, [element, measure])

  return { rect, element }
}
